import React, { useEffect, useState } from 'react';
import { Box, Grid, Typography, Stack, Button, Chip, IconButton, useMediaQuery, useTheme } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MaterialTransportCard from './MaterialTransportCard';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { Formik, Form } from 'formik';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import materialTransportService from '../../service/materialTransport.service';
import MaterialTransportFields from './MaterialTransportFields';
import Yup from '../../utils/yup';
import {
  MaterialTransportItemStatus,
  MaterialTransportOperation,
  MaterialTransportOperationList,
} from '../../config/materialTransport';
import { generateAutocompleteItems } from '../../utils/helper';
import masterDataService from '../../service/masterData.service';
import MaterialTransportItemTable from './MaterialTransportItemTable';
import { ConfirmDialogUI, FileUploadUI } from '../Interface';
import colorFinder from '../../utils/colorFinder';
import { NotificationType } from '../../config';
import addNotification from '../../utils/addNotification';
import { sxConfig } from './materialTransport.style';
import MaterialTransportActions from './MaterialTransportActions';

export const MaterialTransportPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [rows, setRows] = useState([]);
  const [selectedValues, setSelectedValues] = useState({});
  const [initValues, setInitValues] = useState({});
  const [isAdding, setIsAdding] = useState(false);
  const [refetch, setRefetch] = useState(false);
  const [autocompleteData, setAutocompleteData] = useState({});
  const [autocompleteOptions, setAutoCompleteOptions] = useState({
    company: [],
    project: [],
    quantityUnit: [],
  });

  const defConfirmDialogData = {
    open: false,
    message: '',
    handleChangeElement: () => {},
  };
  const [mobileDetailsOpen, setMobileDetailsOpen] = useState(false);
  const [confirmDialogData, setConfirmDialogData] = useState(defConfirmDialogData);
  const [isOrderSaving, setIsOrderSaving] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);

  const defTransportItem = {
    isNew: true,
    visOrder: null,
    itemCode: null,
    qty: null,
    qtyUnitId: null,
    status: MaterialTransportItemStatus.PENDING,
    diffQty: null,
  };

  const defaltValues = {
    visOrder: null,
    companyId: null,
    projectId: null,
    operation: MaterialTransportOperation.PICKUP,
    description: '',
    materialTransportItem: [],
    materialTransportAttachment: [],
  };

  useEffect(() => {
    getTransports();
    setInitValues(defaltValues);
    initDefaultValues();
  }, []);

  useEffect(() => {
    if (refetch) getTransports();
  }, [refetch]);

  useEffect(() => {
    if (Object.keys(selectedValues).length > 0) {
      valuesToForm();
    } else {
      setInitValues({ ...defaltValues, visOrder: rows.length + 1 });
    }
  }, [selectedValues, rows.length]);

  const initDefaultValues = () => {
    const promiseArray = [];

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .companyToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .projectToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .quantityUnitToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    Promise.all(promiseArray)
      .then((resp) => {
        const aData = {
          company: resp[0],
          project: resp[1],
          quantityUnit: resp[2],
        };
        setAutocompleteData(aData);

        const filteredProject = aData.project.filter((p) => p.active);

        const acOptions = {
          company: generateAutocompleteItems(aData.company, 'name', 'id'),
          project: generateAutocompleteItems(filteredProject, 'projectName', 'projectId'),
          quantityUnit: generateAutocompleteItems(aData.quantityUnit, 'code', 'id'),
        };
        setAutoCompleteOptions(acOptions);
      })
      .finally(() => {
        //hideLoader();
      });
  };

  const getTransports = () => {
    materialTransportService.getItems().then((data) => {
      setRows(data);
      setRefetch(false);

      if (!Object.keys(selectedValues).length && !isMobile) {
        setSelectedValues(data[0] ?? {});
      }
    });
  };

  const validationSchema = Yup.object().shape({
    companyId: Yup.string().required(),
    projectId: Yup.string().required(),
    description: Yup.string().nullable(),
    materialTransportItem: Yup.array().of(
      Yup.object().shape({
        itemCode: Yup.string().required(),
        qty: Yup.number().required(),
        qtyUnitId: Yup.string().required(),
        diffQty: Yup.number().nullable(),
      })
    ),
  });

  const valuesToForm = () => {
    const {
      id,
      companyId,
      projectId,
      description,
      visOrder,
      operation,
      materialTransportItem,
      materialTransportAttachment,
    } = selectedValues;

    setInitValues({
      id,
      companyId,
      projectId,
      description,
      visOrder,
      operation,
      materialTransportAttachment,
      materialTransportItem: materialTransportItem?.map((i) => ({
        id: i.id,
        itemCode: i.itemCode,
        itemName: i.item.itemName,
        status: i.status,
        qty: i.qty,
        qtyUnitId: i.qtyUnitId,
        diffQty: i.diffQty,
      })),
    });
  };

  const handleSubmit = (values) => {
    if (values.id) {
      handleUpdate(values);
    } else {
      handleInsert(values);
    }
  };

  const handleInsert = (values) => {
    const formData = new FormData();
    formData.append('data', JSON.stringify(values));

    if (values.materialTransportAttachment) {
      for (const file of values.materialTransportAttachment) {
        formData.append('files', file);
      }
    }

    materialTransportService.createItem(formData).then((data) => {
      setSelectedValues(data);
      setIsAdding(false);
      setRefetch(true);
    });
  };

  const handleUpdate = (values) => {
    values.existingFiles = values.materialTransportAttachment?.map((f) => f.name);

    const formData = new FormData();
    formData.append('data', JSON.stringify(values));

    if (values.materialTransportAttachment) {
      for (const file of values.materialTransportAttachment) {
        if (file instanceof File) {
          formData.append('files', file);
        }
      }
    }

    materialTransportService.updateItem(formData).then((data) => {
      setSelectedValues(data);
      setRefetch(true);
    });
  };

  const handleAddClick = () => {
    setSelectedValues({});
    setIsAdding(true);
    setMobileDetailsOpen(true);
  };

  const handleBackToList = () => {
    setMobileDetailsOpen(false);
    setIsAdding(false);
    setSelectedValues({});
  };

  const handleClose = () => {
    setIsAdding(false);
    setSelectedValues(isMobile ? {} : rows[0] ?? {});
    setMobileDetailsOpen(false);
  };

  const getOperationData = (operationValue) => {
    const findedOperation = MaterialTransportOperationList.find((o) => o.value === operationValue);
    const operationColor = colorFinder(findedOperation?.color);

    return { findedOperation, operationColor };
  };

  const onCloseConfirmDialog = () => {
    setConfirmDialogData(defConfirmDialogData);
  };

  const handleRemoveElement = async (id) => {
    materialTransportService.deleteItem(id).then(() => {
      addNotification({
        content: 'Sikeres törlés!',
        type: NotificationType.WARNING,
      });
      setRefetch(true);
      onCloseConfirmDialog();
      setSelectedValues({});
    });
  };

  const handleDelete = (id) => {
    setConfirmDialogData({
      open: true,
      message: 'Biztosan törölni szeretné?',
      handleChangeElement: () => handleRemoveElement(id),
    });
  };

  const selectedIndex = rows.findIndex((row) => row.id === selectedValues?.id);

  const currentOrder = isAdding ? rows.length + 1 : selectedIndex + 1;
  const totalOrder = isAdding ? rows.length + 1 : rows.length;

  const handleNavigate = (direction) => {
    const nextIndex = selectedIndex + direction;

    if (nextIndex < 0 || nextIndex >= rows.length) return;

    setSelectedValues(rows[nextIndex]);
  };

  const handleDragEnd = async (result) => {
    if (isOrderSaving || !result.destination) return;

    const { source, destination } = result;

    if (source.index === destination.index) return;

    const reorderedRows = [...rows];
    const [movedRow] = reorderedRows.splice(source.index, 1);
    reorderedRows.splice(destination.index, 0, movedRow);

    const updatedRows = reorderedRows.map((row, index) => ({
      ...row,
      visOrder: index + 1,
    }));

    const changedRows = updatedRows
      .filter((row) => row.visOrder !== rows.find((original) => original.id === row.id)?.visOrder)
      .map(({ id, visOrder }) => ({ id, visOrder }));

    setRows(updatedRows);
    setIsOrderSaving(true);

    try {
      await materialTransportService.updateOrder(changedRows);
    } catch (error) {
      setRows(rows);

      addNotification({
        content: 'Nem sikerült menteni a fuvarok sorrendjét!',
        type: NotificationType.ERROR,
      });
    } finally {
      setIsOrderSaving(false);
    }
  };

  const FormDirtySync = ({ dirty, onChange }) => {
    useEffect(() => {
      onChange(dirty);
    }, [dirty, onChange]);

    return null;
  };

  const selectTransport = (row) => {
    setIsAdding(false);
    setSelectedValues(row);
    setMobileDetailsOpen(true);
    onCloseConfirmDialog();
  };

  const handleSelectTransport = (row) => {
    if (isAdding && isFormDirty) {
      setConfirmDialogData({
        open: true,
        message: 'A nem mentett adatok elvesznek. Biztosan másik fuvart szeretne kiválasztani?',
        handleChangeElement: selectTransport,
      });
      return;
    }

    selectTransport(row);
  };

  return (
    <>
      <Grid container spacing={1} sx={sxConfig.page}>
        <Grid
          item
          xs={12}
          md={3}
          sx={{
            ...sxConfig.gridPanel,
            display: { xs: mobileDetailsOpen ? 'none' : 'block', md: 'block' },
          }}
        >
          <Box sx={[sxConfig.panel, sxConfig.leftPanel]}>
            <Typography sx={sxConfig.sectionTitle}>Fuvar sorrend</Typography>

            <Stack spacing={1}>
              {rows.length > 0 ? (
                <DragDropContext onDragEnd={handleDragEnd}>
                  <Droppable droppableId="material-transports">
                    {(provided) => (
                      <Box ref={provided.innerRef} {...provided.droppableProps}>
                        {rows.map((row, index) => (
                          <Draggable
                            key={row.id}
                            draggableId={String(row.id)}
                            index={index}
                            isDragDisabled={isAdding || isOrderSaving}
                          >
                            {(provided) => (
                              <MaterialTransportCard
                                row={row}
                                getOperationData={getOperationData}
                                isSelected={selectedValues.id === row.id}
                                handleSelect={() => handleSelectTransport(row)}
                                handleDelete={handleDelete}
                                dragProvided={provided}
                                deleteDisabled={isAdding || isOrderSaving}
                              />
                            )}
                          </Draggable>
                        ))}

                        {provided.placeholder}
                      </Box>
                    )}
                  </Droppable>
                </DragDropContext>
              ) : (
                <Stack alignItems="center" sx={sxConfig.emptyList}>
                  <LocalShippingIcon fontSize="large" />
                  <Typography fontWeight={600} mb={1}>
                    Még nincs felvett fuvar
                  </Typography>
                </Stack>
              )}
            </Stack>

            <Button
              fullWidth
              variant="outlined"
              startIcon={<AddIcon />}
              sx={[sxConfig.addButton, sxConfig.addCardButton]}
              disabled={isAdding}
              onClick={handleAddClick}
            >
              Kártya hozzáadása
            </Button>
          </Box>
        </Grid>

        <Grid
          item
          xs={12}
          md={9}
          sx={{
            ...sxConfig.gridPanel,
            display: { xs: mobileDetailsOpen ? 'block' : 'none', md: 'block' },
          }}
        >
          <Box sx={[sxConfig.panel, sxConfig.rightPanel]}>
            {isMobile && mobileDetailsOpen && (
              <Button startIcon={<ArrowBackIcon />} onClick={handleBackToList} sx={sxConfig.backToListButton}>
                Vissza a fuvarokhoz
              </Button>
            )}
            {isAdding || Object.keys(selectedValues).length > 0 ? (
              <Formik
                initialValues={initValues}
                validationSchema={validationSchema}
                validateOnChange={false}
                onSubmit={handleSubmit}
                enableReinitialize
              >
                {({ values, setFieldValue, dirty, resetForm }) => {
                  const findedCompany = autocompleteData?.company?.find((c) => c.id === values.companyId);
                  const { findedOperation, operationColor } = getOperationData(values.operation);

                  return (
                    <Form>
                      <FormDirtySync dirty={dirty} onChange={setIsFormDirty} />
                      <Box sx={sxConfig.formContainer}>
                        <Box sx={sxConfig.formContent}>
                          <Stack sx={sxConfig.transportHeader}>
                            <Stack direction="row" alignItems="center" spacing={1} sx={sxConfig.transportHeaderTitle}>
                              <Typography sx={sxConfig.transportTitle}>
                                {`${values.id ? selectedIndex + 1 : values.visOrder}. ${findedCompany?.name ?? ''}`}
                              </Typography>

                              <Chip
                                label={findedOperation?.label}
                                sx={[sxConfig.operationChip(operationColor), sxConfig.transportHeaderChip]}
                              />
                            </Stack>

                            <Stack direction="row" alignItems="center" spacing={1} sx={sxConfig.transportNavigation}>
                              <Typography sx={sxConfig.transportOrderText}>
                                Sorrend: {currentOrder} / {totalOrder}
                              </Typography>

                              <Stack direction="row" alignItems="center" spacing={1}>
                                <IconButton
                                  size="small"
                                  sx={sxConfig.navigationIconButton}
                                  disabled={selectedIndex <= 0}
                                  onClick={() => handleNavigate(-1)}
                                >
                                  <ArrowBackIosIcon />
                                </IconButton>

                                <IconButton
                                  size="small"
                                  sx={sxConfig.navigationIconButton}
                                  disabled={selectedIndex < 0 || selectedIndex >= rows.length - 1}
                                  onClick={() => handleNavigate(1)}
                                >
                                  <ArrowForwardIosIcon />
                                </IconButton>
                              </Stack>
                            </Stack>
                          </Stack>
                          <MaterialTransportFields
                            selectedValues={selectedValues}
                            autocompleteOptions={autocompleteOptions}
                            findedOperation={findedOperation}
                            operationColor={operationColor}
                          />
                          <Box mt={2}>
                            <MaterialTransportItemTable
                              defTransportItem={defTransportItem}
                              autocompleteOptions={autocompleteOptions}
                            />
                          </Box>
                          <Box mt={2}>
                            <Typography sx={sxConfig.sectionTitle}>Csatolmányok</Typography>
                            <FileUploadUI
                              rootSx={{ m: 0 }}
                              value={values.materialTransportAttachment}
                              onFilesChange={(files) =>
                                setFieldValue(
                                  'materialTransportAttachment',
                                  files.map(({ file }) => file)
                                )
                              }
                            />
                          </Box>
                        </Box>
                        <MaterialTransportActions
                          isAdding={isAdding}
                          selectedIndex={selectedIndex}
                          total={rows.length}
                          handleAddClick={handleAddClick}
                          handleNavigate={handleNavigate}
                          handleClose={() => {
                            resetForm();
                            handleClose();
                          }}
                        />
                      </Box>
                    </Form>
                  );
                }}
              </Formik>
            ) : (
              <Stack alignItems="center" justifyContent="center" sx={sxConfig.emptyState}>
                <AssignmentIcon fontSize="large" />
                <Typography fontWeight={600} mb={1}>
                  Nincs kiválasztott fuvar
                </Typography>
              </Stack>
            )}
          </Box>
        </Grid>
      </Grid>
      <ConfirmDialogUI
        handleChangeElement={confirmDialogData.handleChangeElement}
        show={confirmDialogData.open}
        onHide={() => onCloseConfirmDialog()}
        message={confirmDialogData.message}
      />
    </>
  );
};
